import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { FiPlay, FiCopy, FiChevronDown } from 'react-icons/fi';
import APIEndpoints from '../data/apiEndpoints';
import { API_BASE_URL } from '../config';

// Extract path params like <reference_id> from path
function getPathParams(path) {
  const matches = path.match(/<([^>]+)>/g) || [];
  return matches.map(m => m.slice(1, -1));
}

function buildPath(path, pathParamValues) {
  let p = path;
  getPathParams(path).forEach(param => {
    p = p.replace(`<${param}>`, pathParamValues[param] || '');
  });
  return p;
}

function buildCurl(baseUrl, method, path, authType, authValue, queryParams, body) {
  const url = new URL(path, baseUrl);
  if (queryParams && typeof queryParams === 'object') {
    Object.entries(queryParams).forEach(([k, v]) => {
      if (v != null && v !== '') url.searchParams.set(k, String(v));
    });
  }
  let curl = `curl -X ${method} '${url.toString()}'`;
  if (authType === 'bearer' && authValue) {
    curl += ` \\\n  -H "Authorization: Bearer ${authValue.trim()}"`;
  } else if (authType === 'apikey' && authValue) {
    curl += ` \\\n  -H "X-API-Key: ${authValue.trim()}"`;
  }
  if (['POST', 'PUT', 'PATCH'].includes(method) && body) {
    try {
      JSON.parse(body);
      curl += ` \\\n  -H "Content-Type: application/json" \\\n  -d '${body.replace(/'/g, "'\\''")}'`;
    } catch (_) {}
  }
  return curl;
}

function buildSnippets(method, urlStr, headers, bodyJson) {
  const headersObj = headers && typeof headers === 'object' ? headers : {};
  const headersArr = Object.entries(headersObj).filter(([, v]) => v);
  const hasBody = bodyJson && ['POST', 'PUT', 'PATCH'].includes(method);
  const bodyStr = hasBody ? (typeof bodyJson === 'string' ? bodyJson : JSON.stringify(bodyJson, null, 2)) : '';
  const bodyEsc = bodyStr ? bodyStr.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n') : '';
  return {
    curl: (() => {
      let c = `curl -X ${method} '${urlStr}'`;
      headersArr.forEach(([k, v]) => { c += ` \\\n  -H "${k}: ${v}"`; });
      if (bodyStr) c += ` \\\n  -H "Content-Type: application/json" \\\n  -d '${bodyStr.replace(/'/g, "'\\''")}'`;
      return c;
    })(),
    python: `import requests
import json

url = "${urlStr}"
headers = ${JSON.stringify(headersObj)}
${hasBody ? `payload = json.loads("${bodyStr.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n').replace(/\r/g, '')}")\nresponse = requests.${method.toLowerCase()}(url, json=payload, headers=headers)` : `response = requests.${method.toLowerCase()}(url, headers=headers)`}
print(response.json())`,
    javascript: `const response = await fetch("${urlStr}", {
  method: "${method}",
  headers: ${JSON.stringify(headersObj)}${bodyStr ? `,\n  body: JSON.stringify(${bodyStr.trim().startsWith('{') ? bodyStr : JSON.stringify(JSON.parse(bodyStr))})` : ''}
});
const data = await response.json();`,
    php: `<?php\n$ch = curl_init("${urlStr}");\ncurl_setopt($ch, CURLOPT_CUSTOMREQUEST, "${method}");\ncurl_setopt($ch, CURLOPT_HTTPHEADER, [\n${headersArr.map(([k, v]) => `  "${k}: ${v}"`).join(',\n')}\n]);\n${bodyStr ? `curl_setopt($ch, CURLOPT_POSTFIELDS, '${bodyStr.replace(/'/g, "\\'")}');\n` : ''}$response = curl_exec($ch);\ncurl_close($ch);`,
    go: `req, _ := http.NewRequest("${method}", "${urlStr}", nil)\n${headersArr.map(([k, v]) => `req.Header.Set("${k}", "${v}")`).join('\n')}\nclient := &http.Client{}\nres, _ := client.Do(req)\ndefer res.Body.Close()`,
    java: `HttpRequest request = HttpRequest.newBuilder()\n    .uri(URI.create("${urlStr}"))\n    .method("${method}", ${bodyStr ? `HttpRequest.BodyPublishers.ofString("${bodyEsc}")` : 'HttpRequest.BodyPublishers.noBody()'})\n    .header("Content-Type", "application/json")\n    .build();\nHttpResponse<String> response = httpClient.send(request, HttpResponse.BodyHandlers.ofString());`,
    ruby: `require 'net/http'\nuri = URI("${urlStr}")\nreq = Net::HTTP::${method.charAt(0) + method.slice(1).toLowerCase()}.new(uri)\n${headersArr.map(([k, v]) => `req["${k}"] = "${v}"`).join('\n')}\n${bodyStr ? `req.body = '${bodyStr.replace(/'/g, "\\'")}'` : ''}\nres = Net::HTTP.start(uri.hostname, uri.port, use_ssl: uri.scheme == 'https') { |http| http.request(req) }`,
  };
}

// Syntax highlighting for curl (Ultravox/sample_111 style: flag, string, method colors)
function CurlHighlight({ code }) {
  if (!code) return <code />;
  const parts = [];
  let rest = code;
  while (rest.length) {
    const curlMatch = rest.match(/^(\s*)(curl)(\s+)/);
    const flagMatch = rest.match(/^(\s*)(-\w+)(\s+)/);
    const strMatch = rest.match(/^(\s*)('[^']*'|"[^"]*")(\s*)/);
    if (curlMatch) {
      parts.push({ t: 'plain', s: curlMatch[1] }, { t: 'keyword', s: curlMatch[2] }, { t: 'plain', s: curlMatch[3] });
      rest = rest.slice(curlMatch[0].length);
    } else if (flagMatch) {
      parts.push({ t: 'plain', s: flagMatch[1] }, { t: 'flag', s: flagMatch[2] }, { t: 'plain', s: flagMatch[3] });
      rest = rest.slice(flagMatch[0].length);
    } else if (strMatch) {
      parts.push({ t: 'plain', s: strMatch[1] }, { t: 'string', s: strMatch[2] }, { t: 'plain', s: strMatch[3] });
      rest = rest.slice(strMatch[0].length);
    } else {
      const getMatch = rest.match(/^(GET|POST|PUT|PATCH|DELETE)(\s*)/);
      if (getMatch) {
        parts.push({ t: 'method', s: getMatch[1] }, { t: 'plain', s: getMatch[2] });
        rest = rest.slice(getMatch[0].length);
      } else {
        parts.push({ t: 'plain', s: rest[0] });
        rest = rest.slice(1);
      }
    }
  }
  return (
    <code className="code-curl">
      {parts.map((p, i) => (
        <span key={i} className={`code-${p.t}`}>{p.s}</span>
      ))}
    </code>
  );
}

// Python syntax: keyword purple, string orange (Ultravox-style)
function PythonHighlight({ code }) {
  if (!code) return <code />;
  const keywords = ['import', 'from', 'def', 'class', 'return', 'if', 'else', 'elif', 'for', 'while', 'try', 'except', 'with', 'as', 'and', 'or', 'not', 'in', 'True', 'False', 'None'];
  const tokenizeLine = (line) => {
    const parts = [];
    let rest = line;
    while (rest.length) {
      const dq = rest.match(/^"(?:[^"\\]|\\.)*"/);
      const sq = rest.match(/^'(?:[^'\\]|\\.)*'/);
      if (dq) {
        parts.push({ t: 'string', s: dq[0] });
        rest = rest.slice(dq[0].length);
      } else if (sq) {
        parts.push({ t: 'string', s: sq[0] });
        rest = rest.slice(sq[0].length);
      } else {
        let found = false;
        for (const kw of keywords) {
          const regex = new RegExp(`^([\\s\\S]*?)\\b(${kw})\\b`);
          const m = rest.match(regex);
          if (m && m[2]) {
            if (m[1]) parts.push({ t: 'plain', s: m[1] });
            parts.push({ t: 'keyword', s: m[2] });
            rest = rest.slice(m[0].length);
            found = true;
            break;
          }
        }
        if (!found) {
          parts.push({ t: 'plain', s: rest[0] });
          rest = rest.slice(1);
        }
      }
    }
    return parts;
  };
  return (
    <code className="code-python">
      {code.split('\n').map((line, i) => (
        <span key={i}>
          {tokenizeLine(line).map((p, j) => (
            <span key={j} className={`code-${p.t}`}>{p.s}</span>
          ))}
          {i < code.split('\n').length - 1 ? '\n' : ''}
        </span>
      ))}
    </code>
  );
}

// Simple JS highlight: keyword purple, string orange
function JsHighlight({ code }) {
  if (!code) return <code />;
  const keywords = /\b(const|let|var|await|async|function|return|if|else|for|of|in|true|false|null)\b/g;
  const tokenize = (s) => {
    const out = [];
    let rest = s;
    while (rest.length) {
      const str = rest.match(/^("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|`(?:[^`\\]|\\.)*`)/);
      const kw = rest.match(keywords);
      if (str) {
        out.push({ t: 'string', s: str[1] });
        rest = rest.slice(str[1].length);
      } else if (kw) {
        const idx = rest.indexOf(kw[0]);
        if (idx > 0) { out.push({ t: 'plain', s: rest.slice(0, idx) }); rest = rest.slice(idx); }
        out.push({ t: 'keyword', s: kw[0] });
        rest = rest.slice(kw[0].length);
      } else {
        out.push({ t: 'plain', s: rest[0] });
        rest = rest.slice(1);
      }
    }
    return out;
  };
  const lines = code.split('\n');
  return (
    <code className="code-js">
      {lines.map((line, i) => (
        <span key={i}>
          {tokenize(line).map((p, j) => (
            <span key={j} className={`code-${p.t}`}>{p.s}</span>
          ))}
          {i < lines.length - 1 ? '\n' : ''}
        </span>
      ))}
    </code>
  );
}

// JSON highlight: keys blue, strings orange, numbers green
function JsonHighlight({ code }) {
  if (!code) return <code />;
  const out = [];
  let i = 0;
  const re = /"([^"\\]*(?:\\.[^"\\]*)*)"|(-?\d+\.?\d*)|(\{|}|\[|\]|,|:)/g;
  let m;
  let lastIdx = 0;
  let keyNext = true;
  while ((m = re.exec(code)) !== null) {
    if (m.index > lastIdx) out.push(<span key={i++} className="code-plain">{code.slice(lastIdx, m.index)}</span>);
    if (m[1] !== undefined) {
      out.push(<span key={i++} className={keyNext ? 'code-jsonkey' : 'code-string'}>{JSON.stringify(m[1])}</span>);
      keyNext = false;
    } else if (m[2] !== undefined) {
      out.push(<span key={i++} className="code-number">{m[2]}</span>);
      keyNext = false;
    } else {
      out.push(<span key={i++} className="code-plain">{m[3]}</span>);
      if (m[3] === ':') keyNext = false;
      else if (m[3] === ',' || m[3] === '{' || m[3] === '[') keyNext = true;
    }
    lastIdx = re.lastIndex;
  }
  if (lastIdx < code.length) out.push(<span key={i++} className="code-plain">{code.slice(lastIdx)}</span>);
  return <code className="code-json">{out}</code>;
}

function CodeHighlight({ code, language }) {
  if (!code) return <code />;
  if (language === 'curl') return <CurlHighlight code={code} />;
  if (language === 'python') return <PythonHighlight code={code} />;
  if (language === 'javascript') return <JsHighlight code={code} />;
  return <code className="code-highlight">{code}</code>;
}

function CopyIconButton({ text, title }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);
  return (
    <button type="button" className="api-copy-icon-btn" onClick={copy} title={title || 'Copy'} aria-label="Copy">
      {copied ? <span className="api-copy-check">✓</span> : <FiCopy size={16} />}
    </button>
  );
}

// Derive a minimal schema from requestBody so we always show form fields (no raw JSON textarea)
function deriveSchemaFromBody(requestBody) {
  if (!requestBody || typeof requestBody !== 'object') return null;
  const schema = {};
  Object.keys(requestBody).forEach(k => {
    const v = requestBody[k];
    if (Array.isArray(v)) {
      schema[k] = { type: 'array', description: 'JSON array', required: false };
    } else if (v !== null && typeof v === 'object') {
      schema[k] = { type: 'object', description: 'JSON object', required: false };
    } else {
      schema[k] = { type: 'string', description: '', required: false };
    }
  });
  return schema;
}

function getInitialBodyFields(endpoint) {
  const schema = endpoint.requestBodySchema || deriveSchemaFromBody(endpoint.requestBody);
  if (schema && typeof schema === 'object') {
    const out = {};
    Object.keys(schema).forEach(k => { out[k] = ''; });
    return out;
  }
  return null;
}

function buildBodyFromFields(schema, fieldValues) {
  if (!schema || !fieldValues) return null;
  const out = {};
  Object.keys(schema).forEach(k => {
    const raw = fieldValues[k];
    if (raw === undefined || raw === '') return;
    const type = (schema[k].type || '').toLowerCase();
    if (type.includes('array')) {
      try {
        out[k] = JSON.parse(raw);
      } catch (_) {
        out[k] = raw.split(',').map(s => s.trim()).filter(Boolean);
      }
    } else if (type.includes('number') || type.includes('integer')) {
      const n = Number(raw);
      if (!Number.isNaN(n)) out[k] = n;
      else out[k] = raw;
    } else if (type.includes('object')) {
      try {
        out[k] = typeof raw === 'string' ? JSON.parse(raw) : raw;
      } catch (_) {
        out[k] = raw;
      }
    } else {
      out[k] = raw;
    }
  });
  return out;
}

function EndpointCard({ endpoint, sectionKey, index, baseUrl, onTryIt }) {
  const pathParams = getPathParams(endpoint.path);
  const schema = endpoint.requestBodySchema || deriveSchemaFromBody(endpoint.requestBody);
  const useBodyForm = schema && typeof schema === 'object' && Object.keys(schema).length > 0;
  const [authType, setAuthType] = useState('apikey');
  const [authValue, setAuthValue] = useState('');
  const [pathParamValues, setPathParamValues] = useState({});
  const [queryParamValues, setQueryParamValues] = useState({});
  const [bodyFieldValues, setBodyFieldValues] = useState(() => getInitialBodyFields(endpoint) || {});
  const [bodyValue, setBodyValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [tryItModalOpen, setTryItModalOpen] = useState(false);

  const method = endpoint.method.toUpperCase();
  const path = buildPath(endpoint.path, pathParamValues);
  const url = new URL(path, baseUrl);
  if (endpoint.queryParams) {
    endpoint.queryParams.forEach(p => {
      const v = queryParamValues[p.name];
      if (v != null && v !== '') url.searchParams.set(p.name, String(v));
    });
  }

  const handleTryIt = async () => {
    setLoading(true);
    setResponse(null);
    const headers = { 'Content-Type': 'application/json' };
    if (authType === 'bearer' && authValue.trim()) {
      headers['Authorization'] = `Bearer ${authValue.trim()}`;
    } else if (authType === 'apikey' && authValue.trim()) {
      headers['X-API-Key'] = authValue.trim();
    }
    let body = null;
    if (['POST', 'PUT', 'PATCH'].includes(method) && useBodyForm) {
      body = buildBodyFromFields(schema, bodyFieldValues);
      if (body && Object.keys(body).length === 0) body = null;
      if (body) {
        for (const k of Object.keys(schema)) {
          const type = (schema[k].type || '').toLowerCase();
          if (!(type.includes('array') || type.includes('object'))) continue;
          const v = body[k];
          if (typeof v === 'string' && v.trim()) {
            try {
              JSON.parse(v);
            } catch (_) {
              setResponse({ status: 0, error: `Invalid JSON in field "${k}"` });
              setLoading(false);
              return;
            }
          }
        }
      }
    }
    const bodyStrForRequest = body ? JSON.stringify(body) : null;
    const curl = buildCurl(
      baseUrl,
      method,
      path,
      authType,
      authValue,
      endpoint.queryParams ? endpoint.queryParams.reduce((acc, p) => {
        const v = queryParamValues[p.name];
        if (v != null && v !== '') acc[p.name] = v;
        return acc;
      }, {}) : null,
      bodyStrForRequest
    );
    try {
      const res = await fetch(url.toString(), {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        credentials: 'omit',
      });
      const text = await res.text();
      let json = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch (_) {}
      const responseData = {
        status: res.status,
        statusText: res.statusText,
        body: json !== null ? JSON.stringify(json, null, 2) : text,
      };
      setResponse(responseData);
      onTryIt({
        curl,
        response: responseData,
        request: {
          method,
          url: url.toString(),
          headers: { ...headers },
          body: bodyStrForRequest,
        },
      });
    } catch (err) {
      const errMsg = err.message || 'Network error';
      setResponse({ status: 0, error: errMsg });
      onTryIt({
        curl,
        response: { status: 0, error: errMsg },
        request: {
          method,
          url: url.toString(),
          headers: { ...headers },
          body: bodyStrForRequest,
        },
      });
    }
    setLoading(false);
  };

  const needsAuth = endpoint.auth && !endpoint.auth.toLowerCase().includes('public');

  return (
    <div className="api-endpoint" id={`endpoint-${index}`}>
      <div className="api-endpoint-header-row">
        <span className={`api-method ${endpoint.method.toLowerCase()}`}>{endpoint.method}</span>
        <code className="api-path">{endpoint.path}</code>
        <button
          type="button"
          className={`api-tryit-btn ${endpoint.method.toLowerCase()}`}
          onClick={() => setTryItModalOpen(true)}
        >
          <FiPlay size={14} /> Try it
        </button>
      </div>
      {endpoint.description && (
        <p className="api-description">{endpoint.description}</p>
      )}
      {endpoint.auth && (
        <div className="api-section">
          <div className="api-section-title">Authentication</div>
          <p>{endpoint.auth}</p>
        </div>
      )}

      {tryItModalOpen && (
        <div className="api-tryit-modal-overlay" onClick={() => setTryItModalOpen(false)}>
          <div className="api-tryit-modal" onClick={e => e.stopPropagation()}>
            <div className="api-tryit-modal-header">
              <h3 className="api-tryit-modal-title">
                <span className={`api-method ${endpoint.method.toLowerCase()}`}>{method}</span>
                {endpoint.path}
              </h3>
              <button type="button" className="api-tryit-modal-close" onClick={() => setTryItModalOpen(false)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="api-tryit-modal-body">
              <div className="api-tryit-section">
                {needsAuth && (
                  <>
                    <div className="api-section-title">Authorization</div>
                    <div className="api-tryit-auth">
                      <label>Auth type</label>
                      <select value={authType} onChange={e => setAuthType(e.target.value)}>
                        <option value="apikey">X-API-Key</option>
                        <option value="bearer">Bearer Token</option>
                      </select>
                      <label>{authType === 'apikey' ? 'API key' : 'Bearer token'}</label>
                      <input
                        type="password"
                        placeholder={authType === 'apikey' ? 'Enter X-API-Key' : 'Enter Bearer token'}
                        value={authValue}
                        onChange={e => setAuthValue(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                  </>
                )}
                {pathParams.length > 0 && (
                  <>
                    <div className="api-section-title" style={{ marginTop: 12 }}>Path parameters</div>
                    {pathParams.map(param => (
                      <div key={param} className="api-tryit-auth">
                        <label>{param}</label>
                        <input
                          value={pathParamValues[param] || ''}
                          onChange={e => setPathParamValues(prev => ({ ...prev, [param]: e.target.value }))}
                          placeholder={`Enter ${param}`}
                        />
                      </div>
                    ))}
                  </>
                )}
                {endpoint.queryParams && endpoint.queryParams.length > 0 && (
                  <>
                    <div className="api-section-title" style={{ marginTop: 12 }}>Query parameters</div>
                    {endpoint.queryParams.map(p => (
                      <div key={p.name} className="api-tryit-auth">
                        <label>{p.name}</label>
                        <input
                          value={queryParamValues[p.name] || ''}
                          onChange={e => setQueryParamValues(prev => ({ ...prev, [p.name]: e.target.value }))}
                          placeholder={`Enter ${p.name}`}
                        />
                      </div>
                    ))}
                  </>
                )}
                {['POST', 'PUT', 'PATCH'].includes(method) && useBodyForm && (
                  <>
                    <div className="api-section-title" style={{ marginTop: 12 }}>Request body</div>
                    <div className="api-tryit-fields">
                      {Object.entries(schema).map(([key, field]) => {
                        const type = (field.type || '').toLowerCase();
                        const isJsonField = type.includes('array') || type.includes('object');
                        return (
                          <div key={key} className="api-tryit-field">
                            <label>
                              {key}
                              {field.required && <span className="api-param-required">Required</span>}
                            </label>
                            {field.description && <div className="api-tryit-field-desc">{field.description}</div>}
                            <div className="api-tryit-field-meta">
                              {field.type && <span className="api-tryit-field-type">Type: {field.type}</span>}
                            </div>
                            {isJsonField ? (
                              <textarea
                                className="api-tryit-field-json"
                                rows={4}
                                value={bodyFieldValues[key] ?? ''}
                                onChange={e => setBodyFieldValues(prev => ({ ...prev, [key]: e.target.value }))}
                                placeholder={type.includes('array') ? 'e.g. [{"label":"Client","page":1,"x":100,"y":200}]' : 'e.g. {"key": "value"}'}
                                spellCheck={false}
                              />
                            ) : (
                              <input
                                type={key.toLowerCase().includes('password') ? 'password' : 'text'}
                                value={bodyFieldValues[key] ?? ''}
                                onChange={e => setBodyFieldValues(prev => ({ ...prev, [key]: e.target.value }))}
                                placeholder={`Enter ${key}`}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                {response !== null && (
                  <div className="api-response-box" style={{ marginTop: 12 }}>
                    <div className={`api-response-status ${response.status >= 200 && response.status < 300 ? 'ok' : 'err'}`}>
                      {response.status ? `Response ${response.status}` : 'Error'} {response.statusText || response.error}
                    </div>
                    <pre><code>{response.body || response.error}</code></pre>
                  </div>
                )}
              </div>
            </div>
            <div className="api-tryit-modal-footer">
              <button
                type="button"
                className={`api-tryit-btn api-tryit-send-btn ${endpoint.method.toLowerCase()}`}
                onClick={handleTryIt}
                disabled={loading}
              >
                <FiPlay size={14} /> {loading ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}

      {['POST', 'PUT', 'PATCH'].includes(method) && (endpoint.requestBodySchema || endpoint.requestBody) && (
        <div className="api-section">
          <div className="api-section-title">Request Body</div>
          {endpoint.requestBodySchema ? (
            Object.entries(endpoint.requestBodySchema).map(([key, field]) => (
              <div key={key} className="api-param">
                <div className="api-param-name">{key}{field.required && <span className="api-param-required">Required</span>}</div>
                {field.type && <div className="api-param-type">Type: {field.type}</div>}
                {field.description && <div className="api-param-desc">{field.description}</div>}
              </div>
            ))
          ) : (
            Object.keys(endpoint.requestBody || {}).map(key => (
              <div key={key} className="api-param">
                <div className="api-param-name">{key}</div>
                <div className="api-param-type">Type: string</div>
              </div>
            ))
          )}
        </div>
      )}
      {endpoint.queryParams && endpoint.queryParams.length > 0 && (
        <div className="api-section">
          <div className="api-section-title">Query Parameters</div>
          {endpoint.queryParams.map((param, pIdx) => (
            <div key={pIdx} className="api-param">
              <div className="api-param-name">{param.name}{param.required && <span className="api-param-required">Required</span>}</div>
              <div className="api-param-type">Type: {param.type}</div>
              {param.description && <div className="api-param-desc">{param.description}</div>}
            </div>
          ))}
        </div>
      )}
      {endpoint.errors && endpoint.errors.length > 0 && (
        <div className="api-section">
          <div className="api-section-title">Error Responses</div>
          {endpoint.errors.map((error, eIdx) => (
            <div key={eIdx} className="api-param">
              <div className="api-param-name">{error.status} - {error.message}</div>
              {error.description && <div className="api-param-desc">{error.description}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function APIReference() {
  const { section } = useParams();
  const location = useLocation();
  const hash = location.hash || '';
  const hashMatch = hash.match(/^#endpoint-(\d+)$/);
  const selectedEndpointIndex = hashMatch ? Math.max(0, parseInt(hashMatch[1], 10)) : 0;
  const [lastTried, setLastTried] = useState(null);
  const [codeLang, setCodeLang] = useState('curl');
  const [codeLangDropdownOpen, setCodeLangDropdownOpen] = useState(false);

  // When section loads without a hash, set #endpoint-0 so first endpoint is selected and sidebar highlights it
  useEffect(() => {
    if (!section) return;
    const sectionData = APIEndpoints[section];
    if (!sectionData?.endpoints?.length) return;
    const match = hash.match(/^#endpoint-(\d+)$/);
    const idx = match ? parseInt(match[1], 10) : 0;
    const maxIdx = sectionData.endpoints.length - 1;
    const safeIdx = Math.min(Math.max(0, idx), maxIdx);
    const expectedHash = `#endpoint-${safeIdx}`;
    if (location.hash !== expectedHash) {
      window.history.replaceState(null, '', `${location.pathname}${expectedHash}`);
    }
  }, [section, location.pathname]);

  if (!section) {
    return (
      <div className="docs-content">
        <h1>SignTada REST API Overview</h1>
        <p>
          Complete reference for all client-facing SignTada API endpoints. Base URL:
        </p>
        <pre><code>{API_BASE_URL}</code></pre>

        <h2>Authentication</h2>
        <p>
          Most endpoints require authentication. Use one of:
        </p>
        <ul>
          <li><strong>Bearer token:</strong> <code>Authorization: Bearer &lt;access_token&gt;</code> (from login)</li>
          <li><strong>API key:</strong> <code>X-API-Key: sk_live_&lt;your_key&gt;</code> (from dashboard Settings)</li>
        </ul>
        <p>Get your API key from the dashboard under Settings. Do not expose it in client-side code.</p>

        <h2>API Sections</h2>
        <ul>
          <li><Link to="/api-reference/auth">Authentication</Link> - Login, register, refresh, me</li>
          <li><Link to="/api-reference/requests">Document Requests</Link> - Create and manage signing requests</li>
          <li><Link to="/api-reference/templates">Templates</Link> - Reusable document templates</li>
          <li><Link to="/api-reference/webhooks">Webhooks</Link> - Event notifications</li>
          <li><Link to="/api-reference/files">Files</Link> - Upload and download</li>
          <li><Link to="/api-reference/signing">Signing</Link> - Sign and reject documents</li>
          <li><Link to="/api-reference/users">Users</Link> - User management</li>
          <li><Link to="/api-reference/otp">OTP</Link> - Send and verify OTP</li>
          <li><Link to="/api-reference/branding">Branding</Link> - White-label settings</li>
        </ul>
      </div>
    );
  }

  const sectionData = APIEndpoints[section];
  if (!sectionData) {
    return (
      <div className="docs-content">
        <h1>Section Not Found</h1>
        <p>The requested API section does not exist.</p>
      </div>
    );
  }

  const endpoints = sectionData.endpoints || [];
  const selectedEndpoint = endpoints[selectedEndpointIndex] || endpoints[0];
  const defaultCurl = selectedEndpoint?.example || 'Run "Try it" to see cURL.';
  const defaultResponse = selectedEndpoint?.response
    ? JSON.stringify(selectedEndpoint.response, null, 2)
    : 'Run "Try it" to see the response.';

  // Pre-generate snippets from the selected endpoint so all languages show real code by default
  const defaultSnippets = selectedEndpoint
    ? (() => {
        const method = selectedEndpoint.method.toUpperCase();
        const path = selectedEndpoint.path.replace(/<[^>]+>/g, 'value');
        const urlStr = `${API_BASE_URL.replace(/\/$/, '')}${path}`;
        const defaultHeaders = { 'Content-Type': 'application/json' };
        if (selectedEndpoint.auth && !String(selectedEndpoint.auth).toLowerCase().includes('public')) {
          defaultHeaders['X-API-Key'] = '<api-key>';
        }
        return buildSnippets(method, urlStr, defaultHeaders, selectedEndpoint.requestBody);
      })()
    : null;

  const req = lastTried?.request;
  const snippets = req
    ? buildSnippets(req.method, req.url, req.headers, req.body)
    : (defaultSnippets || { curl: defaultCurl, python: defaultCurl, javascript: defaultCurl, php: defaultCurl, go: defaultCurl, java: defaultCurl, ruby: defaultCurl });
  const displayCode = snippets[codeLang] ?? snippets.curl;
  const displayResponse = lastTried?.response
    ? (lastTried.response.body || (lastTried.response.status ? `${lastTried.response.status} ${lastTried.response.statusText}` : lastTried.response.error))
    : defaultResponse;

  const codeLangs = [
    { id: 'curl', label: 'cURL' },
    { id: 'python', label: 'Python' },
    { id: 'javascript', label: 'JavaScript' },
    { id: 'php', label: 'PHP' },
    { id: 'go', label: 'Go' },
    { id: 'java', label: 'Java' },
    { id: 'ruby', label: 'Ruby' },
  ];

  return (
    <div className="docs-content docs-api-layout">
      <div className="docs-api-main">
        <h1>{sectionData.title}</h1>
        <p>{sectionData.description}</p>
        {selectedEndpoint ? (
          <EndpointCard
            key={selectedEndpointIndex}
            endpoint={selectedEndpoint}
            sectionKey={section}
            index={selectedEndpointIndex}
            baseUrl={API_BASE_URL}
            onTryIt={setLastTried}
          />
        ) : (
          <p className="docs-text-muted">Select an endpoint from the sidebar.</p>
        )}
      </div>
      <div className="docs-api-right">
        <div className="api-right-panel">
          <div className="api-right-block">
            <div className="api-right-block-head api-right-block-code-head">
              <div className="api-lang-dropdown">
                <button
                  type="button"
                  className="api-lang-dropdown-trigger"
                  onClick={() => setCodeLangDropdownOpen(o => !o)}
                  aria-expanded={codeLangDropdownOpen}
                  aria-haspopup="listbox"
                >
                  {codeLangs.find(l => l.id === codeLang)?.label || 'cURL'}
                  <FiChevronDown size={14} className="api-lang-chevron" />
                </button>
                {codeLangDropdownOpen && (
                  <>
                    <div className="api-lang-dropdown-backdrop" onClick={() => setCodeLangDropdownOpen(false)} />
                    <ul className="api-lang-dropdown-menu" role="listbox">
                      {codeLangs.map(lang => (
                        <li key={lang.id} role="option" aria-selected={codeLang === lang.id}>
                          <button
                            type="button"
                            className={`api-lang-dropdown-item ${codeLang === lang.id ? 'active' : ''}`}
                            onClick={() => { setCodeLang(lang.id); setCodeLangDropdownOpen(false); }}
                          >
                            {lang.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              <CopyIconButton text={displayCode} title="Copy code" />
            </div>
            <pre className="api-code-pre">
              <CodeHighlight code={displayCode} language={codeLang} />
            </pre>
          </div>
          <div className="api-right-block api-response-block">
            <div className="api-right-block-head">
              {lastTried?.response?.status != null ? lastTried.response.status : '200'}
              <CopyIconButton text={displayResponse} title="Copy response" />
            </div>
            <pre>
              <JsonHighlight code={typeof displayResponse === 'string' ? displayResponse : JSON.stringify(displayResponse, null, 2)} />
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

export default APIReference;
